import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { Card, CardContent } from "../components/ui/card"
import { TeamPickersModal, TeamForModal } from "../components/TeamPickersModal"
import { calculateTeamPoints } from "../utils/scoring"
import { getActiveYear } from "../utils/activeYear"

interface TeamWithPicks {
  id: number
  name: string
  seed: number
  region: string
  wins: number
  is_eliminated: boolean
  pickCount: number
  points: number  // Points earned based on wins
}

type SortField = 'seed' | 'picks' | 'points'
type SortDir = 'asc' | 'desc'

export default function TeamSummary() {
  const [teams, setTeams] = useState<TeamWithPicks[]>([])
  const [loading, setLoading] = useState(true)
  const [sortField, setSortField] = useState<SortField>('seed')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalEntries, setTotalEntries] = useState(0)
  const [hideEliminated, setHideEliminated] = useState(false)
  
  // Modal state
  const [selectedTeam, setSelectedTeam] = useState<TeamForModal | null>(null)
  const navigate = useNavigate()
  
  // Contest access control
  const [contestLive, setContestLive] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    async function loadData() {
      const year = await getActiveYear()
      
      // Check contest_live status
      const { data: yearData } = await supabase
        .from('years')
        .select('contest_live')
        .eq('year', year)
        .single()
      
      setContestLive(yearData?.contest_live ?? false)

      // Check if current user is admin
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single()
        setIsAdmin(profile?.is_admin || false)
      }
      
      // Get all teams from tournament_teams joined with team_schools
      const { data: teamsData } = await supabase
        .from('tournament_teams')
        .select('id, seed, region, wins, is_eliminated, school:team_schools(name)')
        .eq('year', year)

      if (!teamsData) {
        setLoading(false)
        return
      }

      // Get pick counts per team
      const { data: picksData } = await supabase
        .from('entry_picks')
        .select('team_id, entry:entries!inner(year)')
        .eq('entry.year', year)

      // Count entries
      const { count } = await supabase
        .from('entries')
        .select('*', { count: 'exact', head: true })
        .eq('year', year)

      setTotalEntries(count || 0)

      // Count picks per team
      const pickCounts = new Map<number, number>()
      picksData?.forEach((pick: { team_id: number }) => {
        pickCounts.set(pick.team_id, (pickCounts.get(pick.team_id) || 0) + 1)
      })

      // Combine data with points calculation (flatten school name from join)
      const teamsWithPicks = teamsData.map((team: any) => ({
        id: team.id,
        name: team.school?.name || 'Unknown',
        seed: team.seed,
        region: team.region,
        wins: team.wins || 0,
        is_eliminated: team.is_eliminated || false,
        pickCount: pickCounts.get(team.id) || 0,
        points: calculateTeamPoints(team.seed || 1, team.wins || 0)
      }))

      setTeams(teamsWithPicks)
      setLoading(false)
    }

    loadData()
  }, [])

  // Fetch entries for a team when modal opens
  const openTeamModal = (team: TeamWithPicks) => {
    setSelectedTeam({
      id: team.id,
      name: team.name,
      seed: team.seed,
      is_eliminated: team.is_eliminated,
      pickCount: team.pickCount
    })
  }

  const closeModal = () => {
    setSelectedTeam(null)
  }

  const handleEntryClick = (entryId: string) => {
    // Navigate to leaderboard with entry expanded
    navigate(`/leaderboard?expand=${entryId}`)
  }

  // Filter teams by search and eliminated status
  const filteredTeams = teams.filter(t => {
    const matchesSearch = searchQuery === '' || 
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.region.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesAlive = !hideEliminated || !t.is_eliminated
    return matchesSearch && matchesAlive
  })
  
  // Sort teams
  const sortedTeams = [...filteredTeams].sort((a, b) => {
    let comparison = 0
    switch (sortField) {
      case 'seed':
        comparison = a.seed - b.seed
        break
      case 'picks':
        comparison = a.pickCount - b.pickCount
        break
      case 'points':
        comparison = a.points - b.points
        break
    }
    return sortDir === 'desc' ? -comparison : comparison
  })
  
  // Toggle sort direction or change field
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  // No longer grouping by region - removed that sort option

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading teams...</div>
  }

  // Block access if contest is not live (admins can always view)
  if (!contestLive && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Contest Not Started</h2>
        <p className="text-gray-500 mb-6 max-w-md">
          Team picks will be visible once the contest goes live. 
          Check back after entries close!
        </p>
        <a 
          href="/dashboard" 
          className="px-6 py-3 bg-[#1e3a5f] text-white rounded-lg font-medium hover:bg-[#002a6b]"
        >
          Back to Dashboard
        </a>
      </div>
    )
  }

  const aliveTeams = teams.filter(t => !t.is_eliminated).length
  const eliminatedTeams = teams.filter(t => t.is_eliminated).length

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-[#1e3a5f]">Team Summary</h1>
        <p className="text-gray-500 text-sm">
          {aliveTeams} alive • {eliminatedTeams} eliminated • {totalEntries} total entries
        </p>
      </div>

      {/* Search + Toggle */}
      <div className="flex items-center gap-2">
        {/* Search Bar */}
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
          />
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.3-4.3"/>
          </svg>
        </div>

        {/* Hide Eliminated Toggle */}
        <button
          onClick={() => setHideEliminated(!hideEliminated)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            hideEliminated 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-gray-100 text-gray-500 border border-gray-200'
          }`}
        >
          <span>{hideEliminated ? '✓' : ''} Alive</span>
        </button>
      </div>

      {/* Teams List with Column Headers */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-0">
          {/* Column Headers */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500">
            <div className="w-2 flex-shrink-0"></div>
            <SortHeader field="seed" label="Seed" currentField={sortField} dir={sortDir} onSort={handleSort} width="w-8" />
            <div className="flex-1 pl-1">Team</div>
            <SortHeader field="picks" label="Picks" currentField={sortField} dir={sortDir} onSort={handleSort} width="w-12" />
            <SortHeader field="points" label="Pts" currentField={sortField} dir={sortDir} onSort={handleSort} width="w-10" />
            <div className="w-4"></div>
          </div>
          
          {/* No Results */}
          {sortedTeams.length === 0 && (
            <div className="py-12 text-center text-gray-500">
              <p className="font-medium">No teams match your search</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          )}
          
          {sortedTeams.map((team, index) => (
            <TeamRow 
              key={team.id} 
              team={team} 
              isLast={index === sortedTeams.length - 1}
              onClick={() => openTeamModal(team)}
            />
          ))}
        </CardContent>
      </Card>

      {/* Team Entries Modal */}
      {selectedTeam && (
        <TeamPickersModal
          team={selectedTeam}
          onClose={closeModal}
          onEntryClick={handleEntryClick}
        />
      )}
    </div>
  )
}

// Sort Header Component
function SortHeader({ field, label, currentField, dir, onSort, width }: {
  field: SortField
  label: string
  currentField: SortField
  dir: SortDir
  onSort: (field: SortField) => void
  width: string
}) {
  const isActive = field === currentField
  return (
    <button 
      onClick={() => onSort(field)}
      className={`flex items-center justify-center gap-0.5 ${width} ${isActive ? 'text-[#1e3a5f] font-semibold' : 'text-gray-400'}`}
    >
      <span>{label}</span>
      <span className="text-[10px]">{isActive ? (dir === 'desc' ? '↓' : '↑') : ''}</span>
    </button>
  )
}

interface TeamRowProps {
  team: TeamWithPicks
  isLast: boolean
  onClick: () => void
}

function TeamRow({ team, isLast, onClick }: TeamRowProps) {
  return (
    <button 
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left ${!isLast ? 'border-b border-gray-100' : ''}`}
    >
      {/* Status Dot */}
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
        team.is_eliminated ? 'bg-red-400' : 'bg-green-500'
      }`} />

      {/* Seed - Simple text */}
      <div className={`w-6 text-center text-sm font-semibold ${
        team.is_eliminated ? 'text-gray-300' : 'text-[#1e3a5f]'
      }`}>
        {team.seed}
      </div>

      {/* Team Info */}
      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate text-sm ${team.is_eliminated ? 'text-gray-400' : 'text-gray-800'}`}>
          {team.name}
        </div>
        <div className={`text-xs ${team.is_eliminated ? 'text-gray-300' : 'text-gray-400'}`}>
          {team.region} • {team.wins}W
        </div>
      </div>

      {/* Pick Count */}
      <div className="w-10 text-center">
        <span className={`text-sm font-semibold ${team.is_eliminated ? 'text-gray-300' : team.pickCount > 0 ? 'text-[#1e3a5f]' : 'text-gray-300'}`}>
          {team.pickCount}
        </span>
      </div>

      {/* Points */}
      <div className="w-10 text-center">
        <span className={`text-sm font-semibold ${team.is_eliminated ? 'text-gray-300' : team.points > 0 ? 'text-green-600' : 'text-gray-300'}`}>
          {team.points}
        </span>
      </div>

      {/* Arrow */}
      <div className="w-4 flex justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-300">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>
    </button>
  )
}
